/* eslint-disable no-undef */
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { dataDogInit } from 'helpers/dataDog';
import qs from 'query-string';

import {
  AccountManager,
  AnalyticsManager,
  ApiManager,
  Atlas,
  BetManager,
  Bus,
  ExtensionsManager,
  Fuse,
  HistoryManager,
  isNotEmptyObject,
  NotificationManager,
  SettingsManager,
  SiteConfigManager,
  SportManager,
  SubscriptionManager,
  TranslationProvider,
  initDebug,
} from '@atlas/core';
import { COMPONENT_TYPES, staticRoutes } from '@atlas/routing';
import { Messages, TransportManager } from '@atlas/transport';
import { Button, EmptyStateView, Loader } from '@atlas/ui';

import Listen from 'common/Affiltate/init';
import network from 'common/Affiltate/Manager';
import Notifications from 'common/Notifications';
import { CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'common/utils';
import history from 'components/Layout/history';
import MainRouter from 'components/MainRouter/MainRouter';
import GlobalMaintenanceContainer from 'components/MaintenanceContainer/GlobalMaintenanceContainer';
import SeoPageProvider from 'components/SeoPage/SeoPageProvider';
import ToastContainer from 'components/Toast/ToastContainer';
import SelfRegulationProvider from 'components/SelfRegulationProvider';

import 'common/Form/formConfig';
import '../subscriptions/dataLayer.bindings';

import packageData from '../../package.json';
import { resolveFingerprint } from '../fingerprint';
import busEventsMapping from '../subscriptions/bus.bindings';
import subscribeUserOffline from '../subscriptions/subscribeUserOffline';
import Layout from '../terminal/components/Layout';
import { LayoutLoader } from 'src/components/Layout/LayoutLoader';

dataDogInit();
initDebug();

const ShareCss = React.lazy(() => import('modules/share-css'));

import TerminalStaticPage from '../terminal/components/StaticPage/StaticPage';
const Account = React.lazy(() => import('../terminal/pages/Account'));
const Main = React.lazy(() => import('../terminal/pages/Main'));

const PromotionsRouter = React.lazy(() => import('components/Promotion/PromotionsRouter'));
const BlogRouter = React.lazy(() => import('components/Blog/BlogRouter'));
const LuckyDay = React.lazy(() => import('components/LuckyDay/LuckyDay'));
const CmsPageList = React.lazy(() => import('components/CmsPageList/CmsPageList'));

const ComponentLoadingFallback = <Loader className="viewport-height" />;

const ComponentLoadingError = () => (
  <EmptyStateView
    className="viewport-height"
    description="page_loading_error"
    title="page_loading_error_title"
  >
    <Button
      className="block"
      label="refresh"
      onClick={() => window.location.reload()}
      type="primary"
    />
  </EmptyStateView>
);

const SuspenseComponent = Component =>
  function () {
    return (
      <Fuse fallback={ComponentLoadingError}>
        <Suspense fallback={ComponentLoadingFallback}>
          <Component />
        </Suspense>
      </Fuse>
    );
  };

const SuspenseCssComponent = () => (
  <Suspense fallback={<span style={{ display: 'none' }} />}>
    <ShareCss />
  </Suspense>
);

// Updating application default settings using local config
const localLanguage = LocalConfig.get('language');
if (localLanguage) {
  SettingsManager.settings.lang = localLanguage;
}

const {
  blog: blogDefaultPath,
  casino: casinoDefaultPath,
  clientProfile: accountDefaultPath,
  luckyday: luckydayDefaultPath,
  promotions: promotionsDefaultPath,
  sportsbook: sportPrematchDefaultPath,
} = staticRoutes;

const { BLOG, CASINO, CLIENT_PROFILE, LUCKY_DAY, PROMOTIONS, SPORT_PREMATCH } = COMPONENT_TYPES;

SiteConfigManager.setLayoutConfig({
  [BLOG]: {
    component: SuspenseComponent(BlogRouter),
    defaultPath: blogDefaultPath,
    optionalPath: '/:id?',
  },
  [CASINO]: {
    component: SuspenseComponent(Main),
    defaultPath: casinoDefaultPath,
  },
  [CLIENT_PROFILE]: {
    component: SuspenseComponent(Account),
    defaultPath: accountDefaultPath,
  },
  [LUCKY_DAY]: {
    component: SuspenseComponent(LuckyDay),
    defaultPath: luckydayDefaultPath,
  },
  [PROMOTIONS]: {
    component: SuspenseComponent(PromotionsRouter),
    defaultPath: promotionsDefaultPath,
    optionalPath: '/:id?',
  },
  [SPORT_PREMATCH]: {
    component: SuspenseComponent(Main),
    defaultPath: sportPrematchDefaultPath,
  },
  pages: {
    component: SuspenseComponent(CmsPageList),
    defaultPath: '/pages',
  },
});

const providedQueryParams = qs.parse(window.location.search);

const sportsbookCMSConfig = {
  enableBetChecker: false,
  showMultilines: false,
};

const betslipCMSConfig = {
  close_report_in: '0',
  defaultAcceptOddsChangesType: 'NO',
  desktopType: 'classic',
  enableOddsSettings: false,
  flyingBetslip: true,
  maxOutcomes: 1,
  maxOutcomesMode: 'cycle',
  placeBetsBehaviourType: 'default',
  showPreOrder: false,
  showTimeStatus: true,
  single_bet_popup: true,
  validationDisabled: false,
};

const mutateCMSConfig = pageConfig => {
  if (!pageConfig.config) return pageConfig;
  return {
    ...pageConfig,
    config: {
      ...pageConfig.config,
      config: {
        ...pageConfig.config.config,
        registration: {
          ...pageConfig.config.config.registration,
          showInPopup: true,
        },
        bet_sharing: {
          isActive: false,
        },
        betslip: betslipCMSConfig,
        sportsbook: {
          ...pageConfig.config.config.sportsbook,
          ...sportsbookCMSConfig,
        },
      },
    },
  };
};

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line import/extensions, global-require
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(mutateCMSConfig(localCmsSettings.default));

  // Loading svg sprites from dyeing
  fetch('/dyeing/build/sprite/sprite.svg')
    .then(res => res.text())
    .then(svg => {
      document.body.insertAdjacentHTML('beforeend', svg);
    });
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    // FIXME
    SiteConfigManager.setConfig({ ...mutateCMSConfig(page), navigation });
  }
}

BetManager.updateBetslipSettings(betslipCMSConfig);

const defaultAcceptOddChanges = SiteConfigManager.getConfig(
  'config.config.betslip.defaultAcceptOddsChangesType',
);
if (defaultAcceptOddChanges) {
  SettingsManager.settings.acceptOddsChanges = defaultAcceptOddChanges;
}

const defaultOddsZeroes = SiteConfigManager.getConfig('config.config.odds_zeroes');
if (defaultOddsZeroes) {
  SettingsManager.settings.oddsZeroes = defaultOddsZeroes;
}

const defaultOddsFractionDigits = SiteConfigManager.getConfig('config.config.odds_fraction_digits');
if (defaultOddsFractionDigits) {
  SettingsManager.settings.oddsFractionDigits = defaultOddsFractionDigits;
}

const cnf = SiteConfigManager.getConfig('config.config.registry');

if (cnf) {
  network.setConfig(cnf);
  Listen();
}

// Resolving stored settings
SettingsManager.resolveSettings();

busEventsMapping(Bus, history, {
  [Bus.events.profile.realityCheck]: () => {},
});
subscribeUserOffline();

// Initializing api default headers
const defaultHeaders = {
  device: 'desktop',
  version: packageData.version,
};

const projectId = LocalConfig.get('projectId');

// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

SiteConfigManager.device = 'desktop';
// Setting application version & device type
ApiManager.setDefaultHeaders(defaultHeaders);

const { host, protocol } = window.location;
const webSocketProtocol = protocol.startsWith('https') ? 'wss:' : 'ws:';

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('ws', `${webSocketProtocol}//${host}/ws/notifier2`);
ApiManager.setHostname('oddshub', `${webSocketProtocol}//${host}/odds-hub/ws`);
ApiManager.setHostname('media', 'http://app4.front-server.atlas-iac.com');
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));

if (process.env.NODE_ENV === 'development') {
  window.BetManager = BetManager;
  window.Bus = Bus;

  window.AccountManager = AccountManager;
  window.SportManager = SportManager;
  window.NotificationManager = NotificationManager;
  window.SiteConfigManager = SiteConfigManager;
  window.HistoryManager = HistoryManager;
  window.TransportManager = TransportManager.getInstance();
  window.SubscriptionManager = SubscriptionManager;
  window.SettingsManager = SettingsManager;
  window.MOBILE_MAIN_HOST = 'c275x.test.k8s-hz.atlas-iac.com';
}

window.MobileMainLink = `https://${window.MOBILE_MAIN_HOST}`;

if (providedQueryParams.token) {
  ApiManager.setAuthorizationToken(providedQueryParams.token);
  AccountManager.setToken(providedQueryParams.token);
}

SubscriptionManager.authorizationSubscription = Messages.authorizedClientUpdates();

// Creating atlas extensions manager
window.aem = new ExtensionsManager();
// Moving affiltate manager to global scope

const wsUrl = new URL(ApiManager.getHostname('ws'));
wsUrl.searchParams.set('projectId', projectId);
if (process.env.NODE_ENV === 'development') {
  // Added for security reason, special WAF rule created on Cloudflare
  wsUrl.searchParams.set('project-team-frontend-team', 'x');
}
const transport = TransportManager.getInstance();
transport.initialize(wsUrl);
transport.setOptions({
  projectId,
});

// Set Auth Token for API service
ApiManager.setAuthorizationToken(AccountManager.getToken());

// Initializing analytics
AnalyticsManager.init({
  fingerprint: () =>
    resolveFingerprint().then(components => window.btoa(JSON.stringify(components))),
});

const domNode = document.getElementById('root');
const root = createRoot(domNode);

const disabledRoutes = [
  CASINO,
  SiteConfigManager.getComponentPath(CLIENT_PROFILE),
  SiteConfigManager.getExtraConfig(`pathList.${SPORT_PREMATCH}`),
];

root.render(
  <>
    <SuspenseCssComponent />
    <Atlas>
      <TranslationProvider>
        <GlobalMaintenanceContainer>
          <LayoutLoader>
            <Layout>
              <SeoPageProvider disabledRoutes={disabledRoutes}>
                <SelfRegulationProvider>
                  <MainRouter
                    CustomStaticPage={TerminalStaticPage}
                    defaultRedirect={casinoDefaultPath}
                    isShowMessagesPopup={false}
                    withoutSignUpPage={true}
                  />
                </SelfRegulationProvider>
              </SeoPageProvider>
            </Layout>
          </LayoutLoader>
          <Notifications />
          <ToastContainer />
        </GlobalMaintenanceContainer>
      </TranslationProvider>
    </Atlas>
  </>,
);
